import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WebRecordedCommand, WebRecordedStep } from '../Models/web-recorded-command-model';
import { RecordedStepsService } from './recorded-steps.service';
import { RestService } from './rest.service';

// "Save Steps" — persists local recorder steps to the backend. Shared by
// SimulatorToolbarComponent and WebRecorderSessionComponent.closeSession().
@Injectable({ providedIn: 'root' })
export class StepSaveService {

  // "Save Steps" checkbox state — lives here so closeSession() can read it.
  saveStepsChecked = false;

  constructor(
    private restService: RestService,
    private recordedSteps: RecordedStepsService
  ) {}

  // Duplicate-name guard for Save to Canvas / promote only, not the "Save Steps" auto-save.
  // excludeStepId lets promote check "does anyone ELSE have this name".
  checkNameAvailable(projectId: string, stepName: string, excludeStepId?: string): Observable<boolean> {
    return this.restService.get('projectStepsdb2', projectId).pipe(
      map((res: any) => {
        const existingSteps: any[] = (res && res.data) || [];
        const nameTaken = existingSteps.some(s =>
          (s.name || '').trim() === stepName && s['_id'] !== excludeStepId);
        return !nameTaken;
      }),
      catchError(() => of(true))
    );
  }

  // Translates raw recorder command names to tempObject/add's verbose
  // commandmodels vocabulary (e.g. "click" -> "move and click") — matches
  // the reference extension's own playback.js translation table.
  mapCommandToCanvasAction(c: WebRecordedCommand): string {
    switch (c.command) {
      case 'open': return 'open url';
      case 'click': return 'move and click';
      case 'type':
      case 'editContent':
        return 'move click clear and enter text';
      case 'select':
      case 'addSelection':
      case 'removeSelection':
        return 'select box by visible text';
      case 'submit': return 'submit form';
      case 'sendKeys':
        return (c.value || '').includes('${KEY_') ? 'press key' : 'move click clear and enter text';
      case 'doubleClick': return 'double click';
      case 'dragAndDropToObject': return 'drag and drop';

      // Multi-tab recording — see RecorderCaptureService.java's selectWindow/
      // close emission on tab switch/close.
      case 'selectWindow': return 'select window';
      case 'close': return 'close';

      // Context-menu commands — cross-checked against playback.js.
      case 'verifyText': return 'verify text';
      case 'verifyTextContains': return 'verify text contains';
      case 'assertText': return 'assert text';
      case 'assertSelectDropdownText': return 'assert select dropdown text';
      case 'assertCompareImage': return 'assert image snippet';
      case 'storeText': return 'get text';
      case 'storeValue': return 'get value';
      case 'waitForElementPresent': return 'wait for element';
      case 'mouseover': return 'mouse over';
      case 'rightclick': return 'right click';
      case 'dropdownSelect': return 'dropdown select';
      case 'textByKeys': return 'move click clear and enter text by keys';
      case 'findRowByValue': return 'find row by value';
      case 'ifElementPresent': return 'if element present';
      case 'verifyTextInRegion': return 'verify text in region';
      case 'verifyTextContainsInRegion': return 'verify text contains in region';

      // playback.js uses camelCase; our own command ids are lowercase.
      case 'verifyelementchecked': return 'verify element checked';
      case 'verifyelementunchecked': return 'verify element unchecked';
      case 'assertelementchecked': return 'assert element checked';
      case 'assertelementunchecked': return 'assert element unchecked';

      // Closest playback.js equivalent: "verifySelectDropdownText".
      case 'verifyDropdownSelect': return 'verify select dropdown text';

      // "delayed" variants have no playback.js case either — left as
      // pass-through; unmapped ones now fail visibly instead of silently.
      default: return c.command;
    }
  }

  // Saves whatever's new on one step since its last save; resolves to null if nothing's new.
  // source tags the save for the recorder's own list; omit for manual "Save to Canvas".
  saveStep(step: WebRecordedStep, projectId: string, source?: string): Observable<any> {
    const stepName = (step.name || '').trim();
    if (!stepName || !step.commands || step.commands.length === 0) return of(null);

    const alreadySavedCount = step.savedCommandCount || 0;
    const newCommands = step.commands.slice(alreadySavedCount);
    if (newCommands.length === 0) return of(null);

    const payload = {
      projectId,
      steps: [{
        name: stepName,
        source: source,
        // See TempObject.add()'s existingStepId handling — appends onto
        // that step instead of creating a new one.
        existingStepId: step.savedStepId,
        commandsteps: newCommands.map((c, idx) => {
          // "open url" has no target — URL goes in value, not webobjects.
          const isOpen = c.command === 'open';
          // webobject_name= makes Canvas show the readable title instead
          // of the default "Object_N".
          const webobjects = isOpen ? [] : c.targets.slice();
          if (!isOpen && c.title) {
            webobjects.unshift('webobject_name=' + c.title);
          }
          // Re-attach the plain-English sentence RecordedStepsService.
          // appendCommand() pulled out of the raw target array for display —
          // TempObject.java's existingStepId branch reads this exact
          // "englishDescription:" prefix back out of webobjects (see
          // StringLiterals.ENGLISHDESCRIPTION).
          if (c.englishDescription) {
            webobjects.push('englishDescription:' + c.englishDescription);
          }
          return {
            action: this.mapCommandToCanvasAction(c),
            value: isOpen ? (c.targets[0] || '') : (c.isPassword ? '' : c.value),
            webobjects,
            webobjects2: [],
            seq_no: idx,
            ispassword: !!c.isPassword
          };
        })
      }]
    };

    return this.restService.put('tempObject', payload).pipe(
      map((res: any) => {
        // Marks it saved-through-here so the NEXT save only sends the delta.
        const newStepId = res && res.data && res.data.StepId;
        if (newStepId) step.savedStepId = newStepId;
        step.savedCommandCount = step.commands.length;
        return res;
      })
    );
  }

  // Saves every local step with pending commands at session-close time.
  // Resolves once all saves settle, so callers can wait before resetting.
  saveAllStepsWithPendingCommands(projectId: string, source: string): Observable<any[]> {
    const saves = this.recordedSteps.steps.map(step => this.saveStep(step, projectId, source));
    return saves.length ? forkJoin(saves) : of([]);
  }
}
